import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import styles from './OverseasContractInfoSubmitForm.module.scss'
import { useForm, Controller } from 'react-hook-form'
import ToggleButtonGroupController from '../../../components/ToggleButtonGroup/ToggleButtonGroupController'
import SimpleInputController from '../../../components/simpleInput/SimpleInputController'
import IncentiveModal from '../../onboarding/IncentiveModal'
import { formatIncentiveTaxOptionLabel } from '../../../utils/incentiveDisplay'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { Tooltip } from 'react-tooltip'

const OverseasContractInfoSubmitForm = forwardRef(
	(
		{
			onValidationChange,
			isReNegotiation = false,
			hasOverSeasPreferredContract = false,
			overseasContractData = null,
		},
		ref,
	) => {
		// 숫자 포맷팅 함수
		const formatNumber = (value) => {
			if (!value) return ''
			return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
		}

		const { control, handleSubmit, watch, setValue, formState, reset, trigger } = useForm({
			defaultValues: {
				overseas: {
					applyTax: 'exclude', // 세전 (왼쪽 버튼)
					payPeriod: 'weekly', // 주급 (왼쪽 버튼)
					cutPass: '',
					cutOut: '',
					sponsorCap: true, // 착용 (왼쪽 버튼)
					overseasTour: true, // 해외 대회 포함 여부 (해외 폼이므로 항상 true)
					overseasSupport: 0, // 해외대회 지원 항목
					incentiveSettings: [], // 인센티브 설정
					incentiveTaxOption: 'amountDisplayed', // 인센티브 택스 옵션
				},
			},
		})
		const [isModalOpen, setIsModalOpen] = useState(false)

		// 이전 데이터 로드 - useEffect로 한 번만 실행
		useEffect(() => {
			console.log('isReNegotiation: ', isReNegotiation)
			console.log('hasOverSeasPreferredContract: ', hasOverSeasPreferredContract)
			console.log('overseasContractData: ', overseasContractData)
			let parsedData
			try {
				// props로 받은 데이터 사용
				parsedData = overseasContractData
				if (parsedData) {
					console.log('해외 계약 이전 데이터 로드:', parsedData)

					// hasOverSeasPreferredContract가 true일 때만 setValue 실행
					if (hasOverSeasPreferredContract || isReNegotiation) {
						// 각 필드를 개별적으로 업데이트
						setValue(
							'overseas.applyTax',
							parsedData?.isSalaryTaxIncluded ? 'include' : 'exclude',
						)
						setValue(
							'overseas.payPeriod',
							parsedData?.payrollCycles === 'weekly' ? 'weekly' : 'oneTime',
						)
						setValue(
							'overseas.cutPass',
							formatNumber(parsedData?.weeklySalaryCutThrough),
						)
						setValue('overseas.cutOut', formatNumber(parsedData?.weeklySalaryCutFail))
						setValue('overseas.sponsorCap', parsedData?.wearSponsoredHat)
						setValue('overseas.overseasTour', true) // 해외 폼이므로 항상 true
						setValue('overseas.overseasSupport', parsedData?.expenseReimbursement)
						// 드롭다운 선택 상태 업데이트
						if (parsedData?.expenseReimbursement !== undefined) {
							setIsDropdownItemSelected(true)
						}
						setValue(
							'overseas.incentiveSettings',
							parsedData?.incentives
								.sort((a, b) => a.startRank - b.startRank)
								.map((setting, index) => {
									return {
										id: index + 1,
										start: setting.startRank,
										percentage: setting.percentage,
										end: setting.endRank === 0 ? 'CUT' : setting.endRank,
									}
								}),
						)
						setValue(
							'overseas.incentiveTaxOption',
							parsedData?.prizeType === 'displayed'
								? 'amountDisplayed'
								: 'amountReceived',
						)

						console.log('폼 필드 업데이트 완료')

						// 유효성 검사 트리거
						setTimeout(() => {
							trigger()
						}, 100)
					} else {
						console.log(
							'hasOverSeasPreferredContract가 false이므로 폼 필드 업데이트하지 않음',
						)
					}
				}
			} catch (error) {
				console.error('이전 데이터 로드 실패:', error)
			}
		}, [setValue, trigger, isReNegotiation, hasOverSeasPreferredContract, overseasContractData])
		const [isDropdownOpen, setIsDropdownOpen] = useState(false)
		const [isDropdownItemSelected, setIsDropdownItemSelected] = useState(false)
		const dropdownRef = useRef(null)

		// 이전 데이터가 있는 경우 드롭다운 선택 상태 업데이트

		// 토글 옵션들
		const applyTaxOption = [
			{ value: 'exclude', label: '세전' },
			{ value: 'include', label: '세후' },
		]

		const sponsorCapOption = [
			{ value: true, label: '착용' },
			{ value: false, label: '미착용' },
		]

		const overseasTourOption = [
			{ value: false, label: '미포함(국내만)' },
			{ value: true, label: '해외대회 포함' },
		]

		const overseasSupportOptions = [
			{ value: 0, label: '미지원' },
			{ value: 10, label: '10%' },
			{ value: 30, label: '30%' },
			{ value: 50, label: '50%' },
			{ value: 70, label: '70%' },
			{ value: 90, label: '90%' },
			{ value: 100, label: '100%' },
		]

		// 드롭다운 토글 함수
		const toggleDropdown = () => {
			setIsDropdownOpen((prev) => !prev)
		}

		// 드롭다운 아이템 선택 함수
		const handleItemSelect = (e, value) => {
			console.log('value', value)
			e.stopPropagation()
			setValue('overseas.overseasSupport', value)
			setIsDropdownItemSelected(true)
			setIsDropdownOpen(false)
		}

		// 현재 폼 데이터
		const activeData = watch('overseas')
		const isOverseasTour = watch('overseas.overseasTour')
		const overseasSupport = watch('overseas.overseasSupport')
		const incentiveSettings = watch('overseas.incentiveSettings')

		// validation 상태를 상위 컴포넌트로 전달
		useEffect(() => {
			const isValid =
				formState.isValid &&
				incentiveSettings &&
				incentiveSettings.length > 0 &&
				isDropdownItemSelected

			onValidationChange && onValidationChange('overseas', isValid)
		}, [formState.isValid, incentiveSettings, isDropdownItemSelected, onValidationChange])

		// 가격 입력 처리 함수
		const handlePriceInputChange = (e, fieldOnChange) => {
			// 숫자만 입력받도록 처리
			const numericValue = e.target.value.replace(/[^0-9]/g, '')

			// 천원 단위 콤마 적용
			const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

			// 필터링된 값으로 폼 업데이트
			fieldOnChange(formattedValue)
		}

		// 모달 열기 함수
		const handleModalOpen = () => {
			setIsModalOpen(true)
		}

		// 드롭다운 외부 클릭 감지
		useEffect(() => {
			const handleClickOutside = (event) => {
				if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
					setIsDropdownOpen(false)
				}
			}

			if (isDropdownOpen) {
				document.addEventListener('mousedown', handleClickOutside)
			}

			return () => {
				document.removeEventListener('mousedown', handleClickOutside)
			}
		}, [isDropdownOpen])

		// 폼 제출 처리
		const onSubmit = (data) => {
			console.log('폼 데이터:', data)
			// 여기에 제출 로직 추가
		}

		// ref를 통해 부모 컴포넌트에서 호출할 수 있는 메서드들
		useImperativeHandle(ref, () => ({
			getFormData: () => {
				return {
					applyTax: activeData?.applyTax,
					payPeriod: 'weekly',
					periodSalary: activeData?.periodSalary
						? activeData.periodSalary.replace(/,/g, '')
						: '',
					cutPass: activeData?.cutPass ? activeData.cutPass.replace(/,/g, '') : '',
					cutOut: activeData?.cutOut ? activeData.cutOut.replace(/,/g, '') : '',
					sponsorCap: activeData?.sponsorCap,
					overseasTour: activeData?.overseasTour,
					overseasSupport: activeData?.overseasSupport,
					incentiveSettings: activeData?.incentiveSettings,
					incentiveTaxOption: activeData?.incentiveTaxOption,
				}
			},
		}))

		return (
			<div className={styles.contractInfoSubmitFormWrapper}>
				<div className={styles.contractInfoSubmitFormHeader}>
					<div className={styles.domesticIcon}>해외</div>
					<span className={styles.contractInfoSubmitFormTitle}>
						계약 정보를 작성해주세요
					</span>
				</div>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className={styles.contractInfoSubmitFormContainer}>
					<div className={styles.applyTax}>
						<ToggleButtonGroupController
							control={control}
							name="overseas.applyTax"
							toggleFieldLabel="임금 세금 포함 여부"
							toggleOption={applyTaxOption}
							isNoticeNeed={true}
							isNoticeNeedText={`세전 : 3.3% 제외 후 입금 예정\n세후 : 3.3% 제외된 실 입금 금액`}
							styleMode={styles}
						/>
					</div>
					<div className={styles.cutPass}>
						<SimpleInputController
							control={control}
							name="overseas.cutPass"
							rules={{ required: '컷통과 금액을 입력해주세요' }}
							inputLabel="컷통과"
							inputPlaceholder="컷통과 시 지급할 금액을 입력해주세요"
							onChange={handlePriceInputChange}
						/>
					</div>
					<div className={styles.cutOut}>
						<SimpleInputController
							control={control}
							name="overseas.cutOut"
							rules={{ required: '컷탈락 금액을 입력해주세요' }}
							inputLabel="컷탈락"
							inputPlaceholder="컷탈락 시 지급할 금액을 입력해주세요"
							onChange={handlePriceInputChange}
						/>
					</div>
					<div className={styles.incentive}>
						<div className={styles.incentiveInfo}>
							인센티브
							<InformationCircleIcon
								className={styles.noticeIcon}
								data-tooltip-id="my-tooltip11"
								data-tooltip-content={`상금 표기액 : 각 투어별 홈페이지에 명시된 금액 \n 상금 실수령액 : KPGA 10%, KLPGA 9.3% 제외된 금액`}
							/>
							<Tooltip id="my-tooltip11" place="top" className={styles.tooltip} />
						</div>
						<button type="button" onClick={handleModalOpen}>
							입력하기
						</button>
					</div>

					<Controller
						name="overseas.incentiveSettings"
						control={control}
						render={({ field }) => <input type="hidden" {...field} />}
					/>
					{activeData?.incentiveSettings && activeData?.incentiveSettings.length > 0 && (
						<div className={styles.incentiveDisplaySection}>
							<div className={styles.incentiveTaxDisplay}>
								{formatIncentiveTaxOptionLabel(activeData.incentiveTaxOption)}
							</div>
							{activeData.incentiveSettings.map((setting) => {
								if (setting.percentage === null && setting.end === null) return null
								const displayRank =
									setting.id === 1
										? '우승 : '
										: setting.start +
											(setting.end !== null ? ` ~ ${setting.end} :  ` : '~ ')
								const displayPercentage =
									setting.percentage !== null
										? `  ${setting.percentage}%`
										: '미입력'
								return (
									<div key={setting.id} className={styles.incentiveDisplayItem}>
										<div>{displayRank}</div>

										<div>{displayPercentage}</div>
									</div>
								)
							})}
						</div>
					)}
					<div className={styles.overSeasSupport}>
						<div className={styles.overSeasSupportTitle}>해외대회 지원 항목</div>
						<div className={styles.overSeasSupportContainer} ref={dropdownRef}>
							<div
								className={styles.overSeasSupportDropDownInput}
								// ref={dropdownRef}
								onClick={toggleDropdown}
								role="button"
								aria-haspopup="listbox"
								aria-expanded={isDropdownOpen}>
								{!isDropdownItemSelected ? (
									<span className={styles.overSeasSupportDropDownPlaceholder}>
										해외대회 지원 항목을 선택해주세요
									</span>
								) : (
									<span className={styles.overSeasSupportDisplay}>
										{overseasSupport === 0 ? '미지원' : overseasSupport + '%'}
									</span>
								)}
								<ChevronDownIcon className={styles.dropDownArrow} />
							</div>
							{isDropdownOpen && (
								<ul
									className={`${styles.dropDownList} ${isDropdownOpen ? styles.dropDownListOpened : styles.dropDownListClosed}`}>
									{overseasSupportOptions.map((option) => (
										<li
											key={option.value}
											className={styles.dropDownListItem}
											onClick={(e) => handleItemSelect(e, option.value)}>
											{option.label}
										</li>
									))}
								</ul>
							)}
						</div>
					</div>
					<div className={styles.sponsorCap}>
						<ToggleButtonGroupController
							control={control}
							name="overseas.sponsorCap"
							toggleFieldLabel="선수 스폰서 모자 착용"
							toggleOption={sponsorCapOption}
							styleMode={styles}
						/>
					</div>
				</form>

				{/* 인센티브 모달 */}
				<IncentiveModal
					isModalOpen={isModalOpen}
					onFinish={(incentiveTaxOption, incentiveSettings) => {
						setValue('overseas.incentiveSettings', incentiveSettings)
						setValue('overseas.incentiveTaxOption', incentiveTaxOption)
						setIsModalOpen(false)
					}}
					onReturn={() => setIsModalOpen(false)}
					initialSettings={activeData?.incentiveSettings}
				/>
			</div>
		)
	},
)

export default OverseasContractInfoSubmitForm
